import { type IBrokerContext } from '../context'
import { type IBrokerPlugin } from './plugin'
import { type BrokerPluginInstallOption, type BrokerPluginInstallType, type IBrokerPluginInstaller } from './installers/base'
import { type LocalBrokerPluginInstallOption } from './installers/local'
import { type ScriptBrokerPluginInstallOption } from './installers/script'
import { type Logger } from '../utils/logger'

type BrokerPluginInstallOptionType = ScriptBrokerPluginInstallOption | LocalBrokerPluginInstallOption

class BrokerPluginLoader {
  logger: Logger
  installerMap: { [k in BrokerPluginInstallType]?: IBrokerPluginInstaller<k> } = {}

  ctx: IBrokerContext

  constructor (ctx: IBrokerContext) {
    this.ctx = ctx
    this.logger = ctx.logger.createLogger('BrokerPluginLoader')
  }

  loadedPlugins = new Map<string, IBrokerPlugin>()

  addInstaller (type: BrokerPluginInstallType, installer: IBrokerPluginInstaller<BrokerPluginInstallType, any>): this {
    this.installerMap[type] = installer
    this.logger.log(`Added installer for type: ${type}`)
    return this
  }

  async #loadModule<TYPE extends BrokerPluginInstallType>(type: TYPE, config: BrokerPluginInstallOption<TYPE>): Promise<IBrokerPlugin | undefined> {
    const installer = this.installerMap[type]
    if (installer == null) {
      console.error(`Unknown plugin type: ${config.type}`)
      return undefined
    }
    try {
      return await installer.install(this.ctx, config)
    } catch (e) {
      console.error('Error occurred while loading broker plugin', e, { type, config })
      return undefined
    }
  }

  async load (config: BrokerPluginInstallOption<BrokerPluginInstallType>) {
    const module = await this.#loadModule(config.type, config)
    this.logger.log(`Loaded plugin: ${module?.id ?? '<undefined id>'}`, config)
    if (module != null) this.loadedPlugins.set(module.id, module)
  }
}

export {
  BrokerPluginLoader
}
export type {
  BrokerPluginInstallOptionType
}
